import {
  CdkAcknowledgeOptions,
  CdkBootstrapOptions,
  CdkCliTelemetryOptions,
  CdkContextOptions,
  CdkDeployOptions,
  CdkDestroyOptions,
  CdkDiagnoseOptions,
  CdkDiffOptions,
  CdkDocsOptions,
  CdkDoctorOptions,
  CdkDriftOptions,
  CdkFlagsOptions,
  CdkGcOptions,
  CdkGlobalOptions,
  CdkImportOptions,
  CdkInitOptions,
  CdkListOptions,
  CdkLspOptions,
  CdkMetadataOptions,
  CdkMigrateOptions,
  CdkNoticesOptions,
  CdkOrphanOptions,
  CdkPublishAssetsOptions,
  CdkRefactorOptions,
  CdkRollbackOptions,
  CdkSynthOptions,
  CdkValidateOptions,
  CdkWatchOptions,
} from "./cdk-cli-options";

/*******************************************************************************
 *
 * AWS CDK CLI Renderer
 *
 * Pure helpers that turn typed option objects into the corresponding
 * command-line string. Phase 1 covered `cdk deploy`; Phase 2 adds renderers
 * for `synth`, `watch`, `destroy`, `diff`, `bootstrap`, and `list`; Phase 3
 * adds renderers for the maintenance / lifecycle commands `rollback`,
 * `import`, `gc`, `drift`, `refactor`, `publish-assets`, and `orphan`;
 * Phase 4 adds renderers for the inspection / scaffolding commands `init`,
 * `migrate`, `context`, `metadata`, `flags`, `acknowledge`, `notices`,
 * `doctor`, and `docs`; Phase 5 brings the surface level with aws-cdk CLI
 * 2.1136.0 and adds `validate`, `lsp`, `diagnose`, and `cli-telemetry`.
 *
 * Conventions:
 *   - Boolean fields are tri-state: `true` → `--flag`, `false` → `--no-flag`,
 *     `undefined` → nothing.
 *   - Arrays render as repeated `--flag value` pairs (one per entry).
 *   - Records render as repeated `--flag KEY=VALUE` pairs.
 *   - `verbose: n` renders as `-v` repeated `n` times.
 *   - Field order is fixed at compile time so snapshots are stable.
 *   - Trailing positional `stackPatterns` are double-quoted to preserve glob
 *     characters across shells.
 *   - Command-specific positionals (`init.template`, `metadata.stack`,
 *     `acknowledge.id`) render after the command-specific flags, before
 *     the trailing tokens (`-v` repetitions and `stackPatterns`).
 *
 ******************************************************************************/

/**
 * Quote a value for safe inclusion in a shell command. Single quotes are used
 * unless the value contains a single quote itself, in which case the value is
 * wrapped in double quotes with embedded double quotes escaped.
 */
const quote = (value: string): string => {
  if (value === "") {
    return '""';
  }
  // Conservative: prefer no quoting for simple tokens (alphanumerics plus a
  // small set of shell-safe characters); fall back to double quotes otherwise.
  if (/^[A-Za-z0-9_\-./:=@%+,]+$/.test(value)) {
    return value;
  }
  return `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
};

/**
 * Render a tri-state boolean flag. `true` → `--flag`, `false` → `--no-flag`,
 * `undefined` → nothing.
 */
const renderTriBool = (
  flag: string,
  value: boolean | undefined,
): string | undefined => {
  if (value === undefined) {
    return undefined;
  }
  return value ? `--${flag}` : `--no-${flag}`;
};

/**
 * Render a `--flag=value` token. Returns `undefined` when the value is
 * `undefined`.
 */
const renderValue = (
  flag: string,
  value: string | number | undefined,
): string | undefined => {
  if (value === undefined) {
    return undefined;
  }
  return `--${flag}=${quote(String(value))}`;
};

/**
 * Render a repeatable array flag. Each entry produces one `--flag=value`
 * token; an empty or undefined array contributes nothing.
 */
const renderArray = (
  flag: string,
  values: Array<string> | undefined,
): Array<string> => {
  if (!values || values.length === 0) {
    return [];
  }
  return values.map((value) => `--${flag}=${quote(value)}`);
};

/**
 * Render a repeatable record flag. Each entry produces one
 * `--flag=KEY=VALUE` token; an empty or undefined record contributes nothing.
 */
const renderRecord = (
  flag: string,
  values: Record<string, string> | undefined,
): Array<string> => {
  if (!values) {
    return [];
  }
  const keys = Object.keys(values);
  if (keys.length === 0) {
    return [];
  }
  return keys.map((key) => `--${flag}=${quote(`${key}=${values[key]}`)}`);
};

/**
 * Render `verbose: n` as `-v` repeated `n` times.
 */
const renderVerbose = (value: number | undefined): Array<string> => {
  if (value === undefined || value <= 0) {
    return [];
  }
  return Array.from({ length: value }, () => "-v");
};

/**
 * Render the trailing positional stack patterns. Each pattern is double-quoted
 * to preserve glob characters across shells.
 */
const renderStackPatterns = (
  values: Array<string> | undefined,
): Array<string> => {
  if (!values || values.length === 0) {
    return [];
  }
  return values.map((pattern) => `"${pattern}"`);
};

/**
 * Render the shared global flag tokens (everything declared on
 * {@link CdkGlobalOptions} except `verbose` and `stackPatterns`, which are
 * appended last by each renderer).
 */
const renderGlobalTokens = (
  opts: CdkGlobalOptions,
): Array<string | undefined> => [
  renderValue("app", opts.app),
  renderTriBool("asset-metadata", opts.assetMetadata),
  renderValue("build", opts.build),
  renderValue("ca-bundle-path", opts.caBundlePath),
  renderTriBool("ci", opts.ci),
  renderTriBool("color", opts.color),
  ...renderRecord("context", opts.context),
  renderTriBool("debug", opts.debug),
  renderTriBool("debug-app", opts.debugApp),
  renderTriBool("debug-cli", opts.debugCli),
  renderTriBool("ec2creds", opts.ec2creds),
  renderTriBool("ignore-errors", opts.ignoreErrors),
  renderTriBool("json", opts.json),
  renderTriBool("lookups", opts.lookups),
  renderTriBool("no-color", opts.noColor),
  renderTriBool("notices", opts.notices),
  renderValue("output", opts.output),
  renderTriBool("path-metadata", opts.pathMetadata),
  ...renderArray("plugin", opts.plugin),
  renderValue("profile", opts.profile),
  renderValue("proxy", opts.proxy),
  renderValue("region", opts.region),
  renderValue("role-arn", opts.roleArn),
  renderTriBool("staging", opts.staging),
  renderTriBool("strict", opts.strict),
  renderValue("telemetry-file", opts.telemetryFile),
  renderTriBool("trace", opts.trace),
  ...renderArray("unstable", opts.unstable),
  renderTriBool("version-reporting", opts.versionReporting),
  renderTriBool("yes", opts.yes),
];

/**
 * Render the trailing tokens shared by every command — `-v` repetitions and
 * the quoted stack patterns — appended after the command-specific flags.
 */
const renderTrailingTokens = (opts: CdkGlobalOptions): Array<string> => [
  ...renderVerbose(opts.verbose),
  ...renderStackPatterns(opts.stackPatterns),
];

/**
 * Filter undefined / empty tokens and join the surviving tokens with the
 * command name as the leading segment.
 */
const compose = (
  command: string,
  tokens: Array<string | undefined>,
): string => {
  const parts = tokens.filter(
    (token): token is string => token !== undefined && token !== "",
  );
  return [command, ...parts].join(" ");
};

/**
 * Render a `cdk deploy ...` command from the supplied options. Returns the
 * full command string (starting with `deploy`).
 */
export const renderCdkDeploy = (opts: CdkDeployOptions): string => {
  // Global flags first (alphabetised within group), then deploy-specific
  // flags (alphabetised within group), then verbose, then trailing positional
  // stack patterns. Field order is deliberately stable so snapshot tests can
  // anchor on it.
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Deploy-specific flags
    renderTriBool("all", opts.all),
    renderValue("asset-build-concurrency", opts.assetBuildConcurrency),
    renderTriBool("asset-parallelism", opts.assetParallelism),
    renderTriBool("asset-prebuild", opts.assetPrebuild),
    ...renderArray("build-exclude", opts.buildExclude),
    renderValue("change-set-name", opts.changeSetName),
    renderValue("concurrency", opts.concurrency),
    renderTriBool("exclusively", opts.exclusively),
    renderTriBool("execute", opts.execute),
    renderTriBool("express", opts.express),
    renderTriBool("force", opts.force),
    renderTriBool("hotswap", opts.hotswap),
    renderValue(
      "hotswap-ecs-maximum-healthy-percent",
      opts.hotswapEcsMaximumHealthyPercent,
    ),
    renderValue(
      "hotswap-ecs-minimum-healthy-percent",
      opts.hotswapEcsMinimumHealthyPercent,
    ),
    renderValue(
      "hotswap-ecs-stabilization-timeout-seconds",
      opts.hotswapEcsStabilizationTimeoutSeconds,
    ),
    renderTriBool("hotswap-fallback", opts.hotswapFallback),
    renderTriBool("ignore-no-stacks", opts.ignoreNoStacks),
    renderTriBool("import-existing-resources", opts.importExistingResources),
    renderTriBool("logs", opts.logs),
    renderValue("method", opts.method),
    ...renderArray("notification-arns", opts.notificationArns),
    renderValue("outputs-file", opts.outputsFile),
    ...renderArray("parameters", opts.parameters),
    renderTriBool("previous-parameters", opts.previousParameters),
    renderValue("progress", opts.progress),
    renderValue("require-approval", opts.requireApproval),
    renderTriBool("revert-drift", opts.revertDrift),
    renderTriBool("rollback", opts.rollback),
    ...renderRecord("tags", opts.tags),
    renderValue("toolkit-stack-name", opts.toolkitStackName),
    renderTriBool("watch", opts.watch),
    ...renderTrailingTokens(opts),
  ];
  return compose("deploy", tokens);
};

/**
 * Render a `cdk synth ...` command from the supplied options.
 */
export const renderCdkSynth = (opts: CdkSynthOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Synth-specific flags
    renderTriBool("exclusively", opts.exclusively),
    renderTriBool("quiet", opts.quiet),
    renderTriBool("validation", opts.validation),
    ...renderTrailingTokens(opts),
  ];
  return compose("synth", tokens);
};

/**
 * Render a `cdk watch ...` command from the supplied options.
 */
export const renderCdkWatch = (opts: CdkWatchOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Watch-specific flags
    ...renderArray("build-exclude", opts.buildExclude),
    renderValue("change-set-name", opts.changeSetName),
    renderValue("concurrency", opts.concurrency),
    renderTriBool("exclusively", opts.exclusively),
    renderTriBool("force", opts.force),
    renderTriBool("hotswap", opts.hotswap),
    renderValue(
      "hotswap-ecs-maximum-healthy-percent",
      opts.hotswapEcsMaximumHealthyPercent,
    ),
    renderValue(
      "hotswap-ecs-minimum-healthy-percent",
      opts.hotswapEcsMinimumHealthyPercent,
    ),
    renderValue(
      "hotswap-ecs-stabilization-timeout-seconds",
      opts.hotswapEcsStabilizationTimeoutSeconds,
    ),
    renderTriBool("hotswap-fallback", opts.hotswapFallback),
    renderTriBool("logs", opts.logs),
    renderValue("progress", opts.progress),
    renderTriBool("rollback", opts.rollback),
    renderValue("toolkit-stack-name", opts.toolkitStackName),
    ...renderTrailingTokens(opts),
  ];
  return compose("watch", tokens);
};

/**
 * Render a `cdk destroy ...` command from the supplied options.
 */
export const renderCdkDestroy = (opts: CdkDestroyOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Destroy-specific flags
    renderTriBool("all", opts.all),
    renderValue("concurrency", opts.concurrency),
    renderTriBool("exclusively", opts.exclusively),
    renderTriBool("express", opts.express),
    renderTriBool("force", opts.force),
    ...renderTrailingTokens(opts),
  ];
  return compose("destroy", tokens);
};

/**
 * Render a `cdk diff ...` command from the supplied options.
 */
export const renderCdkDiff = (opts: CdkDiffOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Diff-specific flags
    renderTriBool("change-set", opts.changeSet),
    renderValue("context-lines", opts.contextLines),
    renderTriBool("exclusively", opts.exclusively),
    renderTriBool("fail", opts.fail),
    renderTriBool("import-existing-resources", opts.importExistingResources),
    renderTriBool("include-moves", opts.includeMoves),
    renderValue("method", opts.method),
    renderTriBool("processed", opts.processed),
    renderTriBool("quiet", opts.quiet),
    renderTriBool("security-only", opts.securityOnly),
    renderValue("template", opts.template),
    ...renderTrailingTokens(opts),
  ];
  return compose("diff", tokens);
};

/**
 * Render a `cdk bootstrap ...` command from the supplied options.
 */
export const renderCdkBootstrap = (opts: CdkBootstrapOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Bootstrap-specific flags
    renderValue("bootstrap-bucket-name", opts.bootstrapBucketName),
    renderTriBool("bootstrap-customer-key", opts.bootstrapCustomerKey),
    renderValue("bootstrap-kms-key-id", opts.bootstrapKmsKeyId),
    ...renderArray(
      "cloudformation-execution-policies",
      opts.cloudformationExecutionPolicies,
    ),
    renderValue("custom-permissions-boundary", opts.customPermissionsBoundary),
    renderTriBool("deny-external-id", opts.denyExternalId),
    renderTriBool(
      "example-permissions-boundary",
      opts.examplePermissionsBoundary,
    ),
    renderTriBool("execute", opts.execute),
    renderTriBool("express", opts.express),
    renderTriBool("force", opts.force),
    renderTriBool("import-existing-resources", opts.importExistingResources),
    renderTriBool("previous-parameters", opts.previousParameters),
    renderTriBool(
      "public-access-block-configuration",
      opts.publicAccessBlockConfiguration,
    ),
    renderValue("qualifier", opts.qualifier),
    renderTriBool("show-template", opts.showTemplate),
    ...renderRecord("tags", opts.tags),
    renderValue("template", opts.template),
    renderTriBool("termination-protection", opts.terminationProtection),
    renderValue("toolkit-stack-name", opts.toolkitStackName),
    ...renderArray("trust", opts.trust),
    ...renderArray("trust-for-lookup", opts.trustForLookup),
    ...renderArray("untrust", opts.untrust),
    ...renderTrailingTokens(opts),
  ];
  return compose("bootstrap", tokens);
};

/**
 * Render a `cdk list ...` command from the supplied options.
 */
export const renderCdkList = (opts: CdkListOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // List-specific flags
    renderTriBool("long", opts.long),
    renderTriBool("show-dependencies", opts.showDependencies),
    ...renderTrailingTokens(opts),
  ];
  return compose("list", tokens);
};

/**
 * Render a variadic list of trailing positional arguments — `cdk orphan`'s
 * logical IDs and `cdk flags`' feature-flag names. Each entry is double-quoted
 * to preserve any characters the CLI tolerates that shells would otherwise
 * interpret.
 */
const renderQuotedPositionals = (
  values: Array<string> | undefined,
): Array<string> => {
  if (!values || values.length === 0) {
    return [];
  }
  return values.map((value) => `"${value}"`);
};

/**
 * Render a `cdk rollback ...` command from the supplied options.
 */
export const renderCdkRollback = (opts: CdkRollbackOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Rollback-specific flags
    renderTriBool("all", opts.all),
    renderTriBool("force", opts.force),
    ...renderArray("orphan", opts.orphan),
    renderValue("toolkit-stack-name", opts.toolkitStackName),
    renderTriBool("validate-bootstrap-version", opts.validateBootstrapVersion),
    ...renderTrailingTokens(opts),
  ];
  return compose("rollback", tokens);
};

/**
 * Render a `cdk import ...` command from the supplied options.
 */
export const renderCdkImport = (opts: CdkImportOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Import-specific flags
    renderValue("change-set-name", opts.changeSetName),
    renderTriBool("execute", opts.execute),
    renderTriBool("force", opts.force),
    ...renderArray("notification-arns", opts.notificationArns),
    renderValue("record-resource-mapping", opts.recordResourceMapping),
    renderValue("resource-mapping", opts.resourceMapping),
    renderValue("resource-mapping-inline", opts.resourceMappingInline),
    renderTriBool("rollback", opts.rollback),
    renderValue("toolkit-stack-name", opts.toolkitStackName),
    ...renderTrailingTokens(opts),
  ];
  return compose("import", tokens);
};

/**
 * Render a `cdk gc ...` command from the supplied options.
 *
 * The CLI requires the global `--unstable=gc` flag for this command; callers
 * are responsible for including `"gc"` in {@link CdkGlobalOptions.unstable}.
 */
export const renderCdkGc = (opts: CdkGcOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // GC-specific flags
    renderValue("action", opts.action),
    renderValue("bootstrap-stack-name", opts.bootstrapStackName),
    renderTriBool("confirm", opts.confirm),
    renderValue("created-buffer-days", opts.createdBufferDays),
    renderValue("rollback-buffer-days", opts.rollbackBufferDays),
    renderValue("toolkit-stack-name", opts.toolkitStackName),
    renderValue("type", opts.type),
    ...renderTrailingTokens(opts),
  ];
  return compose("gc", tokens);
};

/**
 * Render a `cdk drift ...` command from the supplied options.
 */
export const renderCdkDrift = (opts: CdkDriftOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Drift-specific flags
    renderTriBool("fail", opts.fail),
    ...renderTrailingTokens(opts),
  ];
  return compose("drift", tokens);
};

/**
 * Render a `cdk refactor ...` command from the supplied options.
 */
export const renderCdkRefactor = (opts: CdkRefactorOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Refactor-specific flags
    ...renderArray("additional-stack-name", opts.additionalStackName),
    renderTriBool("dry-run", opts.dryRun),
    renderTriBool("force", opts.force),
    renderValue("override-file", opts.overrideFile),
    renderTriBool("revert", opts.revert),
    renderValue("toolkit-stack-name", opts.toolkitStackName),
    ...renderTrailingTokens(opts),
  ];
  return compose("refactor", tokens);
};

/**
 * Render a `cdk publish-assets ...` command from the supplied options.
 */
export const renderCdkPublishAssets = (
  opts: CdkPublishAssetsOptions,
): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Publish-assets-specific flags
    renderTriBool("all", opts.all),
    renderValue("concurrency", opts.concurrency),
    renderTriBool("exclusively", opts.exclusively),
    renderTriBool("force", opts.force),
    ...renderTrailingTokens(opts),
  ];
  return compose("publish-assets", tokens);
};

/**
 * Render a `cdk orphan ...` command from the supplied options. Positional
 * `logicalIds` render after the command name as quoted, space-separated
 * arguments.
 */
export const renderCdkOrphan = (opts: CdkOrphanOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Orphan has no command-specific flags; trailing tokens carry verbose
    // repetitions and stack patterns, and logical IDs render last.
    ...renderTrailingTokens(opts),
    ...renderQuotedPositionals(opts.logicalIds),
  ];
  return compose("orphan", tokens);
};

/**
 * Render a single positional argument as a quoted token. Returns an empty
 * array when the value is `undefined` so it can be spread into the token
 * list without conditionals.
 */
const renderPositional = (value: string | undefined): Array<string> => {
  if (value === undefined) {
    return [];
  }
  return [`"${value}"`];
};

/**
 * Render a `cdk init ...` command from the supplied options. The positional
 * `template` argument renders after the command-specific flags, before the
 * trailing tokens.
 */
export const renderCdkInit = (opts: CdkInitOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Init-specific flags
    renderValue("from-path", opts.fromPath),
    renderTriBool("generate-only", opts.generateOnly),
    renderValue("language", opts.language),
    renderValue("lib-version", opts.libVersion),
    renderTriBool("list", opts.list),
    renderValue("package-manager", opts.packageManager),
    renderValue("project-name", opts.projectName),
    renderValue("template-path", opts.templatePath),
    // Positional template arg precedes the trailing tokens.
    ...renderPositional(opts.template),
    ...renderTrailingTokens(opts),
  ];
  return compose("init", tokens);
};

/**
 * Render a `cdk migrate ...` command from the supplied options.
 *
 * `--region` is emitted by {@link renderGlobalTokens} rather than here: the
 * CLI declares the flag both globally and on `migrate`, and the surface models
 * it once on `CdkGlobalOptions` so exactly one `--region` token renders.
 */
export const renderCdkMigrate = (opts: CdkMigrateOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Migrate-specific flags
    renderValue("account", opts.account),
    renderTriBool("compress", opts.compress),
    ...renderArray("filter", opts.filter),
    renderValue("from-path", opts.fromPath),
    renderValue("from-scan", opts.fromScan),
    renderTriBool("from-stack", opts.fromStack),
    renderValue("language", opts.language),
    renderValue("output-path", opts.outputPath),
    renderValue("stack-name", opts.stackName),
    ...renderTrailingTokens(opts),
  ];
  return compose("migrate", tokens);
};

/**
 * Render a `cdk context ...` command from the supplied options. The CLI's
 * `--reset` flag takes a context key as its value, so `reset` is rendered
 * as `--reset=KEY` rather than as a tri-state boolean.
 */
export const renderCdkContext = (opts: CdkContextOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Context-specific flags
    renderTriBool("clear", opts.clear),
    renderTriBool("force", opts.force),
    renderValue("reset", opts.reset),
    ...renderTrailingTokens(opts),
  ];
  return compose("context", tokens);
};

/**
 * Render a `cdk metadata ...` command from the supplied options. The
 * positional `stack` argument renders after the command-specific flags
 * (there are none beyond the inherited globals), before the trailing
 * tokens.
 */
export const renderCdkMetadata = (opts: CdkMetadataOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Positional stack arg precedes the trailing tokens.
    ...renderPositional(opts.stack),
    ...renderTrailingTokens(opts),
  ];
  return compose("metadata", tokens);
};

/**
 * Render a `cdk flags ...` command from the supplied options. `--value` is the
 * only value-taking flag on this command; `--set` and the rest are tri-state
 * booleans. Variadic positional feature-flag names render last, after the
 * trailing tokens.
 */
export const renderCdkFlags = (opts: CdkFlagsOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Flags-specific flags
    renderTriBool("all", opts.all),
    renderValue("concurrency", opts.concurrency),
    renderTriBool("default", opts.default),
    renderTriBool("interactive", opts.interactive),
    renderTriBool("recommended", opts.recommended),
    renderTriBool("safe", opts.safe),
    renderTriBool("set", opts.set),
    renderTriBool("unconfigured", opts.unconfigured),
    renderValue("value", opts.value),
    ...renderTrailingTokens(opts),
    ...renderQuotedPositionals(opts.flagNames),
  ];
  return compose("flags", tokens);
};

/**
 * Render a `cdk acknowledge ...` command from the supplied options. The
 * positional `id` argument renders after the command-specific flags (there
 * are none beyond the inherited globals), before the trailing tokens.
 */
export const renderCdkAcknowledge = (opts: CdkAcknowledgeOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Positional notice id precedes the trailing tokens.
    ...renderPositional(opts.id),
    ...renderTrailingTokens(opts),
  ];
  return compose("acknowledge", tokens);
};

/**
 * Render a `cdk notices ...` command from the supplied options.
 */
export const renderCdkNotices = (opts: CdkNoticesOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Notices-specific flags
    renderTriBool("unacknowledged", opts.unacknowledged),
    ...renderTrailingTokens(opts),
  ];
  return compose("notices", tokens);
};

/**
 * Render a `cdk doctor ...` command from the supplied options. `doctor` has
 * no command-specific flags; only the inherited globals and trailing tokens
 * contribute output.
 */
export const renderCdkDoctor = (opts: CdkDoctorOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    ...renderTrailingTokens(opts),
  ];
  return compose("doctor", tokens);
};

/**
 * Render a `cdk docs ...` command from the supplied options.
 */
export const renderCdkDocs = (opts: CdkDocsOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Docs-specific flags
    renderValue("browser", opts.browser),
    ...renderTrailingTokens(opts),
  ];
  return compose("docs", tokens);
};

/**
 * Render a `cdk validate ...` command from the supplied options. The variadic
 * `STACKS` positional is carried by the inherited `stackPatterns` field and
 * renders in trailing-token position.
 */
export const renderCdkValidate = (opts: CdkValidateOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Validate-specific flags
    renderTriBool("online", opts.online),
    renderTriBool("watch", opts.watch),
    ...renderTrailingTokens(opts),
  ];
  return compose("validate", tokens);
};

/**
 * Render a `cdk lsp ...` command from the supplied options. `lsp` takes no
 * positional arguments.
 */
export const renderCdkLsp = (opts: CdkLspOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Lsp-specific flags
    renderTriBool("features", opts.features),
    ...renderTrailingTokens(opts),
  ];
  return compose("lsp", tokens);
};

/**
 * Render a `cdk diagnose ...` command from the supplied options. The variadic
 * `STACKS` positional is carried by the inherited `stackPatterns` field and
 * renders in trailing-token position.
 */
export const renderCdkDiagnose = (opts: CdkDiagnoseOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Diagnose-specific flags
    renderValue("concurrency", opts.concurrency),
    renderValue("toolkit-stack-name", opts.toolkitStackName),
    ...renderTrailingTokens(opts),
  ];
  return compose("diagnose", tokens);
};

/**
 * Render a `cdk cli-telemetry ...` command from the supplied options.
 *
 * `--enable`, `--disable`, and `--status` are mutually exclusive in the CLI.
 * The renderer emits whatever it is given without validating that constraint,
 * matching how the rest of this surface treats upstream `conflicts`
 * declarations.
 */
export const renderCdkCliTelemetry = (opts: CdkCliTelemetryOptions): string => {
  const tokens: Array<string | undefined> = [
    ...renderGlobalTokens(opts),
    // Cli-telemetry-specific flags
    renderTriBool("disable", opts.disable),
    renderTriBool("enable", opts.enable),
    renderTriBool("status", opts.status),
    ...renderTrailingTokens(opts),
  ];
  return compose("cli-telemetry", tokens);
};
